/* Agent Access — Settings section: mint/revoke Clerk User API keys and the
   "Connect your agent" instructions block.

   Keys are created by the USER through Clerk's <APIKeys /> component (User API
   Keys are enabled at the Clerk instance level; there is no dashboard path).
   A key's subject is the user who minted it, which is what makes the
   POST /api/agent-portfolio write path per-user by construction.

   The copy block is the agent's manual: endpoint, auth, payload semantics
   (account upsert + snapshot / holdings full-replace / decisions append).
   Clerk reveals a key's secret at creation, so the block ships with a
   YOUR_API_KEY placeholder the user swaps for the key they just copied —
   we never read key secrets ourselves.

   Shared by mobile Settings (src/v2/v3/Settings.jsx) and desktop Settings
   (src/v3desktop/pages/Settings.jsx); each wraps it in its own Eyebrow. */
import { useState, useEffect } from 'react'
import { APIKeys } from '@clerk/clerk-react'

const Mono = { fontFamily: 'var(--studio-font-mono)' }
const Sans = { fontFamily: 'var(--studio-font-sans)' }

const SYNC_SNIPPET = `# Atlas Agent Sync — paste into your agent's setup
# Create an API key in Atlas → Settings → Agent Access, then
# replace YOUR_API_KEY below with the key you copied.

POST https://stonk.world/api/agent-portfolio
Authorization: Bearer YOUR_API_KEY
Content-Type: application/json

# Body — send any subset of the three keys:
{
  "account": { "cash": 200, "buying_power": 200, "equity": 200 },
  "holdings": [
    { "ticker": "NVDA", "qty": 2, "avg_cost": 180.10,
      "last_price": 204.89, "market_value": 409.78 }
  ],
  "decisions": [
    { "ticker": "NVDA", "action": "BUY", "qty": 2, "price": 180.10,
      "reasoning": "why the agent did this", "order_id": "abc",
      "placed": true }
  ]
}

# Semantics:
# - account:   upserted (one row per user); every account-carrying sync
#              also appends a P/L history snapshot
# - holdings:  FULL REPLACE of your position mirror — send the complete
#              list every sync; [] means no positions
# - decisions: append-only log; Atlas annotates each entry with its own
#              rating + conviction read server-side
# Sync after every agent run. Writes only ever touch YOUR account —
# the key itself determines the user. The mirror renders at
# My Stocks -> MY AGENT.
# Same key also maintains the My Stocks portfolio:
#   POST /api/portfolio?action=save  (see that endpoint's actions)`

// Clerk appearance — blend the hosted component into the v3 surface tokens.
// Clerk `variables` must be literal colors (no CSS vars), so the appearance
// is a function of theme: day = the warm studio palette, night = the violet
// [data-theme="night"] palette from v3.css. Previously day values were frozen,
// leaving an amber-on-warm-black widget inside the violet Night shell.
const clerkAppearance = (night) => ({
  variables: night ? {
    colorBackground: 'hsl(247 22% 11%)',
    colorInputBackground: 'hsl(247 20% 15%)',
    colorText: 'hsl(247 46% 89%)',
    colorTextSecondary: 'hsl(247 26% 52%)',
    colorPrimary: 'hsl(247 65% 64%)',
    colorDanger: 'hsl(0 72% 55%)',
    borderRadius: '6px',
    fontSize: '13px',
  } : {
    colorBackground: 'hsl(30 14% 6%)',
    colorInputBackground: 'hsl(30 14% 9%)',
    colorText: 'hsl(40 41% 91%)',
    colorTextSecondary: 'hsl(40 12% 60%)',
    colorPrimary: '#C89B3C',
    colorDanger: 'hsl(0 72% 55%)',
    borderRadius: '6px',
    fontSize: '13px',
  },
  elements: {
    rootBox: { width: '100%' },
    cardBox: { width: '100%', boxShadow: 'none', border: night ? '1px solid hsl(247 18% 20%)' : '1px solid hsl(30 10% 16%)' },
  },
})

// Tracks [data-theme] on <html> (theme.js sets/removes it; there's no event,
// so observe the attribute). Re-renders the Clerk widget on Day↔Night flips.
function useIsNightTheme() {
  const [night, setNight] = useState(() => typeof document !== 'undefined' && document.documentElement.dataset.theme === 'night')
  useEffect(() => {
    const el = document.documentElement
    const mo = new MutationObserver(() => setNight(el.dataset.theme === 'night'))
    mo.observe(el, { attributes: true, attributeFilter: ['data-theme'] })
    return () => mo.disconnect()
  }, [])
  return night
}

export function AgentAccess() {
  const night = useIsNightTheme()
  const [copied, setCopied] = useState(false)
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(SYNC_SNIPPET)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch { /* clipboard unavailable — the block is selectable text anyway */ }
  }

  return (
    <div>
      <p style={{ ...Sans, fontSize: 12, lineHeight: 1.55, color: 'hsl(var(--studio-text-subtle))', margin: '0 0 12px', textWrap: 'pretty' }}>
        Give your trading agent its own key to the Agent Desk. Keys are yours alone &mdash;
        anything synced with one lands on your page only. Revoke a key here at any time
        to cut the agent off instantly.
      </p>

      {/* Clerk-managed key minting/revocation — the secret is shown once at creation */}
      <APIKeys appearance={clerkAppearance(night)} />

      {/* The agent's manual — one copy, paste into the agent's setup */}
      <div style={{ marginTop: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <span style={{ ...Mono, fontSize: 9.5, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'hsl(var(--studio-accent))' }}>Connect your agent</span>
          <button onClick={copy} style={{
            ...Mono, fontSize: 9.5, letterSpacing: '0.1em', cursor: 'pointer', padding: '4px 10px',
            background: 'transparent', color: copied ? 'hsl(var(--studio-success))' : 'hsl(var(--studio-accent))',
            border: `1px solid hsl(var(${copied ? '--studio-success' : '--studio-accent'}) / 0.45)`,
            borderRadius: 'var(--studio-radius-sm)', transition: 'color 150ms, border-color 150ms',
          }}>{copied ? 'COPIED ✓' : 'COPY'}</button>
        </div>
        <pre className="atlas-scroll" style={{
          ...Mono, fontSize: 10, lineHeight: 1.6, color: 'hsl(var(--studio-text-muted))',
          background: 'hsl(var(--studio-surface) / 0.5)', border: '1px solid hsl(var(--studio-border))',
          borderRadius: 'var(--studio-radius)', padding: '12px 14px', margin: 0,
          overflowX: 'auto', whiteSpace: 'pre',
        }}>{SYNC_SNIPPET}</pre>
        <p style={{ ...Sans, fontSize: 11, lineHeight: 1.5, color: 'hsl(var(--studio-text-subtle))', margin: '8px 0 0', textWrap: 'pretty' }}>
          Copy the block, swap in the key you minted above, and give it to your agent.
          That&rsquo;s the whole integration &mdash; the Agent Desk fills in on its next sync.
        </p>
      </div>

      <SetupGuide />
    </div>
  )
}

/* ── the full agent setup guide — the front half of the manual. The connect
   block above documents the LAST step (write to Atlas); this covers everything
   before it: getting an agent that can read your brokerage in the first
   place. Collapsed by default so the section stays compact. ── */
function Step({ n, title, children }) {
  return (
    <div style={{ display: 'flex', gap: 12, padding: '12px 0', borderTop: '1px solid hsl(var(--studio-border) / 0.4)' }}>
      <span style={{ ...Mono, fontSize: 10, color: 'hsl(var(--studio-accent))', flexShrink: 0, width: 16, paddingTop: 2 }}>{n}</span>
      <div style={{ minWidth: 0 }}>
        <div style={{ ...Sans, fontSize: 13, color: 'hsl(var(--studio-text))' }}>{title}</div>
        <div style={{ ...Sans, fontSize: 11.5, lineHeight: 1.55, color: 'hsl(var(--studio-text-muted))', marginTop: 4, textWrap: 'pretty' }}>{children}</div>
      </div>
    </div>
  )
}

function SetupGuide() {
  const [open, setOpen] = useState(false)
  const [advanced, setAdvanced] = useState(false)
  return (
    <div style={{ marginTop: 18 }}>
      <button onClick={() => setOpen((o) => !o)} style={{
        display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: 0, cursor: 'pointer',
        background: 'transparent', border: 'none', textAlign: 'left',
      }}>
        <span style={{ ...Mono, fontSize: 9.5, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'hsl(var(--studio-accent))' }}>Full setup guide</span>
        <span style={{ ...Mono, fontSize: 10, color: 'hsl(var(--studio-accent-soft))' }}>{open ? '−' : '+'}</span>
      </button>

      {open && (
        <div style={{ marginTop: 10 }}>
          <p style={{ ...Sans, fontSize: 11.5, lineHeight: 1.55, color: 'hsl(var(--studio-text-subtle))', margin: '0 0 10px', textWrap: 'pretty' }}>
            Starting from zero: what you need is your own Claude account, the Claude
            desktop app (its Claude Code tab), your brokerage login, and this page.
            Four steps.
          </p>

          <Step n="1" title="Connect your agent to your brokerage">
            In a Claude Code session, add your brokerage&rsquo;s official agent connector
            &mdash; for Robinhood, ask Claude to register the MCP server at
            {' '}<span style={{ ...Mono, fontSize: 10.5 }}>agent.robinhood.com/mcp/trading</span>{' '}
            and then run <span style={{ ...Mono, fontSize: 10.5 }}>/mcp</span> to sign in with
            YOUR brokerage login. Your agent can now read your positions. Atlas is never
            part of this step &mdash; it never sees your brokerage credentials.
          </Step>

          <Step n="2" title="Mint your Atlas key">
            At the top of this section: create an API key and copy the secret when it&rsquo;s
            shown (Clerk shows it once). The key is tied to your Atlas login &mdash; anything
            synced with it lands on your page only, and you can revoke it here anytime.
          </Step>

          <Step n="3" title="Install the Atlas Agent Kit plugin">
            In the agent&rsquo;s Claude Code session, run
            {' '}<span style={{ ...Mono, fontSize: 10.5 }}>/plugin marketplace add benjaminwolf36/atlas-agent-kit</span>{' '}
            then <span style={{ ...Mono, fontSize: 10.5 }}>/plugin install atlas-agent@atlas-agent-kit</span>.
            That one install carries everything the agent needs to know: the guardrails
            (which fetch the caps and kill switch you set in the Agent Desk&rsquo;s gear panel
            before every order), the sync contract, and the strategy playbooks. Give it the
            key you just minted and tell it to store it in machine-local config &mdash; never
            inside a code repository or a shared chat. No plugin? The manual fallback is the
            &ldquo;Connect your agent&rdquo; block above with YOUR_API_KEY swapped in.
          </Step>

          <Step n="4" title="Sync">
            Have your agent run the sync after each session (or on a schedule): account
            state, full holdings list, any new decisions with reasoning. Your Agentic page
            (My Stocks &rarr; MY AGENT) fills in on the first sync and stays current from there.
          </Step>

          <button onClick={() => setAdvanced((a) => !a)} style={{
            display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '12px 0 0', cursor: 'pointer',
            background: 'transparent', border: 'none', borderTop: '1px solid hsl(var(--studio-border) / 0.4)',
            marginTop: 2, textAlign: 'left',
          }}>
            <span style={{ ...Mono, fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'hsl(var(--studio-text-subtle))' }}>Advanced · sharing one Claude account</span>
            <span style={{ ...Mono, fontSize: 10, color: 'hsl(var(--studio-text-subtle))' }}>{advanced ? '−' : '+'}</span>
          </button>
          {advanced && (
            <div style={{ ...Sans, fontSize: 11.5, lineHeight: 1.6, color: 'hsl(var(--studio-text-muted))', marginTop: 8, textWrap: 'pretty' }}>
              Two people on one Claude account can keep separate brokerages and separate
              Atlas pages &mdash; but only with machine-local setup. Register the brokerage MCP
              inside each computer&rsquo;s local Claude Code config (step 1 above, on each
              machine, each person signing into their own brokerage), and never connect the
              brokerage through the account-level Connectors settings &mdash; that path stores
              ONE login for the whole account, on every device. Each person mints their own
              Atlas key from their own Atlas login. Brokerage work then only happens in
              local sessions on each machine &mdash; phones and cloud sessions intentionally
              have no brokerage access. When in doubt: separate Claude accounts make all of
              this automatic.
            </div>
          )}

          <p style={{ ...Sans, fontSize: 10.5, lineHeight: 1.55, color: 'hsl(var(--studio-text-subtle))', margin: '14px 0 0', textWrap: 'pretty' }}>
            The Agentic page is a mirror &mdash; your agent pushes what it sees, Atlas never
            calls a brokerage and never holds brokerage credentials. Ratings and conviction
            tags on decisions are Atlas&rsquo;s own research output, not advice.
          </p>
        </div>
      )}
    </div>
  )
}
